import { createElement, Fragment } from "@wordpress/element";

/**
 * External dependencies
 */
import classnames from 'classnames';
import { sortBy } from 'lodash';
/**
 * WordPress dependencies
 */

import { BlockControls, useBlockProps, getColorClassName } from '@wordpress/block-editor';
import { ToolbarButton, Spinner, Notice } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useMemo, useState, memo } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { store as coreStore, useEntityRecords } from '@wordpress/core-data';
/**
 * Internal dependencies
 */

import ConvertToLinksModal from './convert-to-links-modal';
import { ItemSubmenuIcon } from '../navigation-link/icons'; // We only show the edit option when page count is <= MAX_PAGE_COUNT
// Performance of Navigation Links is not good past this value.

const MAX_PAGE_COUNT = 100;
export default function PageListEdit(_ref) {
  var _context$style;

  let {
    context,
    clientId
  } = _ref;
  const {
    pagesByParentId,
    totalPages,
    hasResolvedPages
  } = usePageData();
  const isNavigationChild = ('showSubmenuIcon' in context);
  const allowConvertToLinks = isNavigationChild && totalPages <= MAX_PAGE_COUNT;
  const [isOpen, setOpen] = useState(false);

  const openModal = () => setOpen(true);

  const closeModal = () => setOpen(false);

  const blockProps = useBlockProps({
    className: classnames('wp-block-page-list', {
      'has-text-color': !!context.textColor,
      [getColorClassName('color', context.textColor)]: !!context.textColor,
      'has-background': !!context.backgroundColor,
      [getColorClassName('background-color', context.backgroundColor)]: !!context.backgroundColor
    }),
    style: { ...((_context$style = context.style) === null || _context$style === void 0 ? void 0 : _context$style.color)
    }
  });
  return createElement(Fragment, null, allowConvertToLinks && createElement(BlockControls, {
    group: "other"
  }, createElement(ToolbarButton, {
    title: __('Edit'),
    onClick: openModal
  }, __('Edit'))), allowConvertToLinks && isOpen && createElement(ConvertToLinksModal, {
    onClose: closeModal,
    clientId: clientId
  }), !hasResolvedPages && createElement("div", blockProps, createElement(Spinner, null)), hasResolvedPages && totalPages === null && createElement("div", blockProps, createElement("div", blockProps, createElement(Notice, {
    status: 'warning',
    isDismissible: false
  }, __('Page List: Cannot retrieve Pages.')))), totalPages === 0 && createElement("div", blockProps, createElement(Notice, {
    status: 'info',
    isDismissible: false
  }, __('Page List: Cannot retrieve Pages.'))), totalPages > 0 && createElement("ul", blockProps, createElement(PageItems, {
    context: context,
    pagesByParentId: pagesByParentId
  })));
}

function useFrontPageId() {
  return useSelect(select => {
    const site = select(coreStore).getEntityRecord('root', 'site');
    return (site === null || site === void 0 ? void 0 : site.show_on_front) === 'page' && (site === null || site === void 0 ? void 0 : site.page_on_front);
  }, []);
}

function usePageData() {
  const {
    records: pages,
    hasResolved: hasResolvedPages
  } = useEntityRecords('postType', 'page', {
    orderby: 'menu_order',
    order: 'asc',
    _fields: ['id', 'link', 'parent', 'title', 'menu_order'],
    per_page: -1
  });
  return useMemo(() => {
    var _pages$length;

    // TODO: Once the REST API supports passing multiple values to
    // 'orderby', this can be removed.
    // https://core.trac.wordpress.org/ticket/39037
    const sortedPages = sortBy(pages, ['menu_order', 'title.rendered']);
    const pagesByParentId = sortedPages.reduce((accumulator, page) => {
      const {
        parent
      } = page;

      if (accumulator.has(parent)) {
        accumulator.get(parent).push(page);
      } else {
        accumulator.set(parent, [page]);
      }

      return accumulator;
    }, new Map());
    return {
      pagesByParentId,
      hasResolvedPages,
      totalPages: (_pages$length = pages === null || pages === void 0 ? void 0 : pages.length) !== null && _pages$length !== void 0 ? _pages$length : null
    };
  }, [pages, hasResolvedPages]);
}

const PageItems = memo(function PageItems(_ref2) {
  let {
    context,
    pagesByParentId,
    parentId = 0,
    depth = 0
  } = _ref2;
  const pages = pagesByParentId.get(parentId);
  const frontPageId = useFrontPageId();

  if (!(pages !== null && pages !== void 0 && pages.length)) {
    return [];
  }

  return pages.map(page => {
    var _page$title, _page$title2;

    const hasChildren = pagesByParentId.has(page.id);
    const isNavigationChild = ('showSubmenuIcon' in context);
    return createElement("li", {
      key: page.id,
      className: classnames('wp-block-pages-list__item', {
        'has-child': hasChildren,
        'wp-block-navigation-item': isNavigationChild,
        'open-on-click': context.openSubmenusOnClick,
        'open-on-hover-click': !context.openSubmenusOnClick && context.showSubmenuIcon,
        'menu-item-home': page.id === frontPageId
      })
    }, hasChildren && context.openSubmenusOnClick ? createElement(Fragment, null, createElement("button", {
      className: "wp-block-navigation-item__content wp-block-navigation-submenu__toggle",
      "aria-expanded": "false"
    }, (_page$title = page.title) === null || _page$title === void 0 ? void 0 : _page$title.rendered), createElement("span", {
      className: "wp-block-page-list__submenu-icon wp-block-navigation__submenu-icon"
    }, createElement(ItemSubmenuIcon, null))) : createElement("a", {
      className: classnames('wp-block-pages-list__item__link', {
        'wp-block-navigation-item__content': isNavigationChild
      }),
      href: page.link
    }, (_page$title2 = page.title) === null || _page$title2 === void 0 ? void 0 : _page$title2.rendered), hasChildren && createElement(Fragment, null, !context.openSubmenusOnClick && context.showSubmenuIcon && createElement("button", {
      className: "wp-block-navigation-item__content wp-block-navigation-submenu__toggle wp-block-page-list__submenu-icon wp-block-navigation__submenu-icon",
      "aria-expanded": "false"
    }, createElement(ItemSubmenuIcon, null)), createElement("ul", {
      className: classnames('submenu-container', {
        'wp-block-navigation__submenu-container': isNavigationChild
      })
    }, createElement(PageItems, {
      context: context,
      pagesByParentId: pagesByParentId,
      parentId: page.id,
      depth: depth + 1
    }))));
  });
});
//# sourceMappingURL=edit.js.map